/** Merit points (02 §11). The original's values are undocumented; these are the remake's. */

import { isPrisoner } from './objectives/common';
import { PROPS } from './props';
import { OBJECTIVES, SCORE } from './tuning';
import { type EnemyKind, type MissionDef, type MissionResult, type Outcome, type ScoreLine, TICK_RATE, type World } from './types';

export function scoreMission(w: World, def: MissionDef, outcome: Outcome): MissionResult {
  const lines: ScoreLine[] = [];
  // OBJ: the verdict is the objective system's (`world.objective`), never recomputed here.
  const o = w.objective;
  const objectiveComplete = o.complete && !o.failed;
  const extracted = outcome === 'extracted';
  const secondsLeft = Math.floor(w.clockTicks / TICK_RATE);

  if (objectiveComplete) {
    // OBJ: a wounded prisoner is worth half (02 §11, mission 4).
    const hurt = o.captives.some((c) => isPrisoner(c.kind) && c.wounded);
    lines.push({
      label: hurt ? 'OBJECTIVE ACHIEVED - PRISONER WOUNDED' : 'OBJECTIVE ACHIEVED',
      points: SCORE.objective * (hurt ? OBJECTIVES.score.woundedPrisoner : 1),
    });
  }
  if (def.objective.kind === 'destroyTargets' && o.done > 0) {
    lines.push({ label: `TARGETS DESTROYED x${o.done}`, points: o.done * SCORE.partial });
  }
  // OBJ: every POW or hostage who went up the ladder counts, whatever became of the rest.
  const rescued = o.captives.filter((c) => !isPrisoner(c.kind) && c.state === 'boarded').length;
  if (rescued > 0) lines.push({ label: `BROUGHT OUT ALIVE x${rescued}`, points: rescued * OBJECTIVES.score.captive });
  // R4: the span dropped under the convoy (10 §2, mission 13) - the deed the Silver Star is written for.
  if (o.spanDeed) lines.push({ label: 'THE SPAN DROPPED UNDER THE CONVOY', points: SCORE.spanDeed });
  if (extracted) lines.push({ label: 'RANGER RETRIEVED', points: SCORE.extracted });

  // Incapacitated soldiers count exactly like kills: there is never a reason to finish the wounded.
  const tally: Record<EnemyKind, number> = {
    rifleman: 0, elite: 0, officer: 0, flamethrower: 0, rocketTrooper: 0, miniBunker: 0, minitank: 0,
    truck: 0, staffCar: 0, railEngine: 0, launcherTruck: 0, dog: 0,
  };
  // Mission 11 leans on destruction (03 §2): emplacements, and the men who were serving them, are worth x1.5.
  const emplacementWorth = def.archetype === 'diversion' ? SCORE.diversionEmplacement : 1;
  let crews = 0;
  let crewPoints = 0;
  for (const e of w.enemies) {
    if (e.state !== 'dead' && e.state !== 'wounded') continue;
    // A minitank that blew itself up on him did the garrison's work, not his: it is not on the bill.
    if (e.selfDestroyed) continue;
    tally[e.kind]++;
    const post = e.mans === null ? undefined : def.props.find((p) => p.id === e.mans);
    if (post && PROPS[post.kind].emplacement === true) {
      crews++;
      crewPoints += SCORE[e.kind];
    }
  }
  const LABELS: Record<EnemyKind, string> = {
    rifleman: 'ENEMY SOLDIERS', elite: 'ELITE TROOPS', officer: 'OFFICERS', flamethrower: 'FLAMETHROWERS',
    rocketTrooper: 'ROCKET TROOPERS', miniBunker: 'AUTOMATED BUNKERS', minitank: 'MINITANKS',
    truck: 'TROOP CARRIERS', staffCar: 'STAFF CARS', railEngine: 'LOCOMOTIVES', launcherTruck: 'MOBILE LAUNCHERS',
    dog: 'TRACKER DOGS',
  };
  // OBJ: premature contact is "a penalty kills can never offset" (02 §11): they are listed, and worth nothing.
  const killWorth = o.contactBroken ? 0 : 1;
  for (const kind of Object.keys(tally) as EnemyKind[]) {
    const points = tally[kind] * SCORE[kind] * killWorth;
    if (tally[kind] > 0) lines.push({ label: `${LABELS[kind]} x${tally[kind]}`, points });
  }
  if (emplacementWorth !== 1 && crews > 0) {
    const points = Math.round(crewPoints * (emplacementWorth - 1)) * killWorth;
    lines.push({ label: `GUN CREWS x${crews} - DIVERSION BONUS`, points });
  }
  if (o.contactBroken) lines.push({ label: 'PREMATURE CONTACT', points: OBJECTIVES.score.breach });
  const bonusOffered = def.objective.rules.some((rule) => rule.type === 'noContactBonus');
  if (bonusOffered && extracted && objectiveComplete && !o.contactBroken) {
    lines.push({ label: 'NO CONTACT', points: OBJECTIVES.score.noContact });
  }
  if (w.emplacementsDestroyed > 0) {
    const label = `EMPLACEMENTS x${w.emplacementsDestroyed}${emplacementWorth !== 1 ? ` (x${emplacementWorth})` : ''}`;
    lines.push({ label, points: Math.round(w.emplacementsDestroyed * SCORE.emplacement * emplacementWorth) });
  }
  if (extracted && secondsLeft > 0) lines.push({ label: `TIME IN HAND ${secondsLeft}s`, points: secondsLeft * SCORE.perSecondLeft });
  if (extracted && objectiveComplete && w.maxAlert <= 1) lines.push({ label: 'GHOST: NEVER ABOVE WARY', points: SCORE.ghostBonus });

  const multiplier = 1 + 0.15 * (def.threat - 1);
  const subtotal = lines.reduce((sum, l) => sum + l.points, 0);
  return {
    outcome,
    objectiveComplete,
    lines,
    multiplier,
    total: Math.max(0, Math.round(subtotal * multiplier)),
    secondsLeft,
  };
}
